import { useCallback, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import googleAnalyticsService from 'src/core/analytics/GoogleAnalyticsService'
import InitializationServiceCore from 'src/core/classes/services/InitializationServiceCore'
import { hubPool } from 'src/core/classes/ws/signalr/HubPool'
import { AppLoader } from 'src/core/components/app/AppLoader'
import { AppContext } from 'src/core/components/app/context/AppContext'
import { useInitAuth } from 'src/core/hooks/services/useInitAuth'
import { useSitePages } from 'src/core/hooks/services/useSitePages'
import { useAppDispatch } from 'src/core/store'
import { setAuthError } from 'src/core/store/appSlice'
import { useFetchSitePagesQuery } from 'src/core/store/service/sitebuilder/pageApi'
import { useFetchAllRolesQuery } from 'src/core/store/service/sitebuilder/roleApi'
import { useFetchSettingsQuery } from 'src/core/store/service/sitebuilder/storageApi'
import { useFetchUserQuery, useLogoutMutation } from 'src/core/store/service/sitebuilder/userApi'
import { useFetchWidgetsQuery } from 'src/core/store/service/sitebuilder/widgetApi'

import { ICoreLoaderWrapper } from './models/ICoreLoaderWrapper'

export const CoreLoaderWrapperInner = ({
  customRoutes = [],
  beforeInitCallback,
  children,
  initializeHubs = true,
}: ICoreLoaderWrapper) => {
  const id = useSelector(selectAppId)
  const ownerId = useSelector(selectAppOwnerId)
  const user = useSelector(selectUser)
  const { userSettings } = useContext(AppContext)
  const { initAuth, authState } = useInitAuth()
  const [isAppInit, setAppInit] = useState<boolean>(false)

  const availableGuestAuthorization = userSettings?.availableGuestAuthorization || false

  const settingsQueryConfig = { skip: authState !== 'auth' }
  const { isError: isFetchUserError, isSuccess: isFetchUserSuccess } = useFetchUserQuery(
    undefined,
    settingsQueryConfig
  )
  const { isError: isSettingsError, isSuccess: isSettingsSuccess } = useFetchSettingsQuery(
    undefined,
    settingsQueryConfig
  )
  const { isError: isRolesError, isSuccess: isRolesSuccess } = useFetchAllRolesQuery(
    undefined,
    settingsQueryConfig
  )
  const { isError: isWidgetsError, isSuccess: isWidgetsSuccess } = useFetchWidgetsQuery(
    undefined,
    settingsQueryConfig
  )

  const sitePageQueryConfig = { skip: !isRolesSuccess }
  const { isError: isSitePagesError, isSuccess: isSitePagesSuccess } = useFetchSitePagesQuery(
    false,
    sitePageQueryConfig
  )

  const [logout] = useLogoutMutation()

  const dispatch = useAppDispatch()

  useSitePages()

  const setErrorState = useCallback(
    (message: string) => {
      setAppInit(true)
      dispatch(
        setAuthError({
          result: true,
          message,
          code: '401',
        })
      )
    },
    [dispatch]
  )

  const finishInitialization = useCallback(async () => {
    if (beforeInitCallback) {
      await beforeInitCallback()
    }
    setAppInit(true)
  }, [beforeInitCallback])

  useEffect(() => {
    const initialize = async () => {
      if (id && ownerId) {
        try {
          await initAuth(availableGuestAuthorization, customRoutes)
        } catch (e) {
          setErrorState((e as Error).message)
        }
      } else {
        setErrorState('Application was not defined')
      }
    }

    initialize()
  }, [id, ownerId, customRoutes, initAuth, availableGuestAuthorization, dispatch, setErrorState])

  useEffect(() => {
    if (authState === 'login') {
      finishInitialization()
    }
  }, [authState, finishInitialization])

  useEffect(() => {
    if (isFetchUserError || isSettingsError) {
      // maybe show error?
      logout()
    }
  }, [isFetchUserError, isSettingsError, logout])

  useEffect(() => {
    const getMessage = (type: string) => {
      return `Problem with ${type} fetching. Try to reload page`
    }

    if (isRolesError) {
      return setErrorState(getMessage('roles'))
    }
    if (isWidgetsError) {
      return setErrorState(getMessage('widgets'))
    }
    if (isSitePagesError) {
      return setErrorState(getMessage('site pages'))
    }
  }, [isRolesError, isSitePagesError, isWidgetsError, setErrorState])

  useEffect(() => {
    if (
      isSettingsSuccess &&
      isFetchUserSuccess &&
      isRolesSuccess &&
      isWidgetsSuccess &&
      user.email
    ) {
      InitializationServiceCore.init()
      googleAnalyticsService.init(user)
      if (initializeHubs) {
        hubPool.init()
      }
    }
  }, [
    user,
    isFetchUserSuccess,
    isRolesSuccess,
    isSettingsSuccess,
    isWidgetsSuccess,
    initializeHubs,
  ])

  useEffect(() => {
    if (isSitePagesSuccess && isSettingsSuccess && isFetchUserSuccess && isWidgetsSuccess) {
      finishInitialization()
    }
  }, [
    finishInitialization,
    isFetchUserSuccess,
    isSettingsSuccess,
    isSitePagesSuccess,
    isWidgetsSuccess,
  ])

  if (!id || !ownerId) {
    return <div>Empty Application id or ownerId</div>
  }

  if (!isAppInit) {
    return <AppLoader />
  }

  return <>{children}</>
}
